import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Smartphone,
  Shield,
  Users,
  Bell,
  Cloud,
  CheckCircle,
  ArrowRight,
  Check,
  X,
  Layers,
} from "lucide-react";
import "./MobileAppDevelopment.css";
import { subscribeToNewsletter } from "./newsletterService";

export default function MobileAppDevelopment() {
  const [activeTab, setActiveTab] = useState("native");
  const [isScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const fallbackPalette = ["#7c3aed", "#2563eb", "#f97316", "#10b981", "#ec4899", "#0ea5e9"];

  const getInitials = (name) =>
    name
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const makeFallbackSvg = (label, color) => {
    const safeLabel = getInitials(label);
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <defs>
          <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stop-color="${color}" stop-opacity="0.9" />
            <stop offset="100%" stop-color="${color}" stop-opacity="0.7" />
          </linearGradient>
        </defs>
        <rect x="4" y="4" width="56" height="56" rx="14" fill="url(#g)" />
        <text x="32" y="38" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="#ffffff">
          ${safeLabel}
        </text>
      </svg>
    `;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  const startProject = () => {
    window.location.href = "/#contact";
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.newsletterEmail?.value || "";
    const result = subscribeToNewsletter(email);
    window.alert(result.message);
    if (result.ok) {
      form.reset();
    }
  };

  const technologies = {
    native: [
      {
        name: "Swift",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/swift/swift-original.svg",
        color: "#FA7343",
      },
      {
        name: "Kotlin",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg",
        color: "#7F52FF",
      },
      {
        name: "Java",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg",
        color: "#007396",
      },
      {
        name: "Xcode",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/xcode/xcode-original.svg",
        color: "#147EFB",
      },
      {
        name: "Android Studio",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/androidstudio/androidstudio-original.svg",
        color: "#3DDC84",
      },
      {
        name: "Objective-C",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/objectivec/objectivec-plain.svg",
        color: "#438EFF",
      },
    ],
    crossplatform: [
      {
        name: "React Native",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg",
        color: "#61DAFB",
      },
      {
        name: "Flutter",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg",
        color: "#02569B",
      },
      {
        name: "Ionic",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/ionic/ionic-original.svg",
        color: "#3880FF",
      },
      {
        name: "Xamarin",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/xamarin/xamarin-original.svg",
        color: "#3498DB",
      },
      {
        name: "Capacitor",
        logo: "https://capacitorjs.com/docs/img/meta/capacitor-og.png",
        color: "#119EFF",
      },
      {
        name: "Cordova",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cordova/cordova-original.svg",
        color: "#E8E8E8",
      },
    ],
    backend: [
      {
        name: "Firebase",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg",
        color: "#FFCA28",
      },
      {
        name: "AWS",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
        color: "#FF9900",
      },
      {
        name: "Node.js",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg",
        color: "#339933",
      },
      {
        name: "MongoDB",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg",
        color: "#47A248",
      },
      {
        name: "PostgreSQL",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg",
        color: "#4169E1",
      },
      {
        name: "GraphQL",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/graphql/graphql-plain.svg",
        color: "#E10098",
      },
    ],
  };

  const whatWeOffer = [
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Native App Development",
      description:
        "High-performance iOS and Android apps built with platform-specific languages for optimal user experience.",
    },
    {
      icon: <Layers className="w-6 h-6" />,
      title: "Cross-Platform Apps",
      description:
        "Cost-effective solutions using React Native or Flutter to deploy on both iOS and Android simultaneously.",
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cloud Integration",
      description:
        "Seamless backend integration with cloud services for data sync, storage, and real-time updates.",
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: "Push Notifications",
      description:
        "Engaging notification systems to keep users informed and boost app engagement and retention.",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Social Integration",
      description:
        "Connect users through social media login, sharing capabilities, and community features.",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "App Security",
      description:
        "Enterprise-grade security with encryption, secure authentication, and data protection measures.",
    },
  ];

  const whyChooseUs = [
    "Native and cross-platform expertise",
    "App Store and Play Store optimization",
    "Offline functionality support",
    "Real-time data synchronization",
    "Secure payment gateway integration",
    "Analytics and user behavior tracking",
    "Regular updates and maintenance",
    "Post-launch support and monitoring",
  ];

  const packages = [
    {
      name: "MVP",
      price: "From R20,000",
      ideal: "Startups & validation",
      features: [
        { text: "Single platform (iOS or Android)", included: true },
        { text: "Core features only", included: true },
        { text: "Basic UI/UX design", included: true },
        { text: "Backend API integration", included: true },
        { text: "App Store submission", included: true },
        { text: "Cross-platform support", included: false },
        { text: "Advanced animations", included: false },
        { text: "Custom integrations", included: false },
      ],
      popular: false,
    },
    {
      name: "Full-Featured",
      price: "From R50,000",
      ideal: "Growing businesses",
      features: [
        { text: "Both iOS & Android", included: true },
        { text: "Complete feature set", included: true },
        { text: "Premium UI/UX design", included: true },
        { text: "Custom backend development", included: true },
        { text: "Both store submissions", included: true },
        { text: "Push notifications", included: true },
        { text: "Social media integration", included: true },
        { text: "Analytics integration", included: true },
      ],
      popular: true,
    },
    {
      name: "Enterprise",
      price: "From R150,000",
      ideal: "Large scale solutions",
      features: [
        { text: "iOS, Android & Web", included: true },
        { text: "Advanced features", included: true },
        { text: "Custom design system", included: true },
        { text: "Scalable cloud infrastructure", included: true },
        { text: "White-label solutions", included: true },
        { text: "Advanced security features", included: true },
        { text: "Third-party integrations", included: true },
        { text: "Dedicated support team", included: true },
      ],
      popular: false,
    },
  ];

  const process = [
    {
      step: "1",
      title: "Consultation & Planning",
      description:
        "We analyze your requirements, target audience, and define the app's core functionality and user flows.",
    },
    {
      step: "2",
      title: "UI/UX Design",
      description:
        "Creating intuitive, beautiful interfaces with wireframes, mockups, and interactive prototypes.",
    },
    {
      step: "3",
      title: "Development",
      description:
        "Building your app with clean code, following platform guidelines and best practices for performance.",
    },
    {
      step: "4",
      title: "Testing & QA",
      description:
        "Comprehensive testing on multiple devices and OS versions to ensure flawless functionality.",
    },
    {
      step: "5",
      title: "Deployment & Launch",
      description:
        "App Store and Play Store submission, approval process management, and successful launch.",
    },
  ];

  const appTypes = [
    {
      type: "E-Commerce Apps",
      icon: "shopping-cart",
      description:
        "Shopping apps with secure payments and inventory management",
    },
    {
      type: "Social Media Apps",
      icon: "comment-dots",
      description: "Community platforms with chat, feeds, and user profiles",
    },
    {
      type: "On-Demand Services",
      icon: "car",
      description: "Uber-like apps for services, delivery, and bookings",
    },
    {
      type: "Health & Fitness",
      icon: "dumbbell",
      description: "Wellness apps with tracking, workouts, and nutrition",
    },
    {
      type: "Education & Learning",
      icon: "graduation-cap",
      description: "E-learning platforms with courses and progress tracking",
    },
    {
      type: "Entertainment",
      icon: "gamepad",
      description: "Gaming, streaming, and media consumption apps",
    },
  ];

  const appShowcase = [
    {
      src: "https://images.unsplash.com/photo-1551650975-87deedd944c3?auto=format&fit=crop&w=1200&q=80",
      title: "Polished Mobile Interfaces",
      tag: "UX Design",
    },
    {
      src: "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=1200&q=80",
      title: "Cross-Platform Build Quality",
      tag: "React Native / Flutter",
    },
    {
      src: "https://images.unsplash.com/photo-1526498460520-4c246339dccb?auto=format&fit=crop&w=1200&q=80",
      title: "Reliable Production Apps",
      tag: "Launch Ready",
    },
  ];

  return (
    <div className="mobile-app-page">
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
        <div className="container navbar-container">
          <a href="/" className="navbar-brand">
            <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
          </a>

          <div className={`navbar-menu ${mobileMenuOpen ? "active" : ""}`}>
            <div className="services-dropdown">
              <div className="navbar-link active-link">Services</div>
              <div className="dropdown-menu">
                <Link to="/web-development" className="dropdown-item">
                  <i className="fas fa-laptop-code"></i>
                  Web Development Services
                </Link>
                <Link to="/mobile-app" className="dropdown-item active-link">
                  <i className="fas fa-mobile-alt"></i>
                  Mobile App Development
                </Link>
                <Link to="/custom-software" className="dropdown-item">
                  <i className="fas fa-cogs"></i>
                  Custom Software Development
                </Link>
                <Link to="/api-backend" className="dropdown-item">
                  <i className="fas fa-plug"></i>
                  API & Backend Services
                </Link>
                <Link to="/maintenance" className="dropdown-item">
                  <i className="fas fa-tools"></i>
                  Website & App Maintenance
                </Link>
                <Link to="/ai-services" className="dropdown-item">
                  <i className="fas fa-robot"></i>
                  AI & Machine Learning Services
                </Link>
              </div>
            </div>
            {/* <a href="/#work" className="navbar-link">Work</a> */}
            <a href="/#about" className="navbar-link">
              About
            </a>
{/*
            <a href="/#testimonials" className="navbar-link">
              Testimonials
            </a>
*/}
            <a href="/#faq" className="navbar-link">
              FAQ
            </a>
            <a href="/#contact" className="navbar-link">
              Contact
            </a>
            <Link to="/track-project" className="navbar-link">
              Track Project
            </Link>
            <button className="btn btn-primary nav-consult-btn" onClick={startProject}>
              Request Consultation
            </button>
          </div>

          <button
            className="navbar-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}></i>
          </button>
        </div>
      </nav>
      {/* Hero Section */}
      <section className="mobile-app-hero">
        <div className="mobile-app-container">
          <div className="mobile-app-breadcrumb">
            <a href="/">Home</a> / <a href="/#services">Services</a> /{" "}
            <span className="current">Mobile App Development</span>
          </div>
          <h1 className="mobile-app-title">Mobile App Development</h1>
          <p className="mobile-app-hero-description">
            Build secure mobile platforms that improve customer experience and
            support measurable business outcomes for Johannesburg and South
            African businesses across iOS and Android.
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="mobile-app-section">
        <div className="mobile-app-container">
          <div className="mobile-app-overview-grid">
            <div>
              <h2 className="mobile-app-section-title-left">
                Mobile Platforms Built for Reliability
              </h2>
              <p className="mobile-app-overview-text">
                Mobile applications are critical channels for customer service,
                operations, and growth. We deliver intuitive, feature-rich
                mobile products that are stable, secure, and designed for
                long-term use while serving businesses across Johannesburg.
              </p>
              <p className="mobile-app-overview-text">
                Whether you need native iOS, Android, or cross-platform
                delivery, our team manages the full lifecycle from planning and
                architecture to release and post-launch support.
              </p>
            </div>
            <div className="mobile-app-benefits-card">
              <h3 className="mobile-app-benefits-title">Why Mobile Platforms?</h3>
              <div className="mobile-app-benefits-list">
                {whyChooseUs.map((benefit, idx) => (
                  <div key={idx} className="mobile-app-benefit-item">
                    <CheckCircle className="mobile-app-benefit-icon" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section className="mobile-app-section">
        <div className="mobile-app-container">
          <h2 className="mobile-app-section-title-center">
            Mobile Experiences Built for Trust
          </h2>
          <p className="mobile-app-section-subtitle">
            We combine performance, clarity, and visual polish so your app feels
            native and trustworthy from first launch.
          </p>
          <div className="mobile-app-showcase-grid">
            {appShowcase.map((item, idx) => (
              <article key={idx} className="mobile-app-showcase-card">
                <img src={item.src} alt={item.title} className="mobile-app-showcase-image" />
                <div className="mobile-app-showcase-overlay">
                  <span className="mobile-app-showcase-tag">{item.tag}</span>
                  <h3 className="mobile-app-showcase-title">{item.title}</h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="mobile-app-section mobile-app-bg-light">
        <div className="mobile-app-container">
          <h2 className="mobile-app-section-title-center">
            Our Mobile Solutions
          </h2>
          <p className="mobile-app-section-subtitle">
            Enterprise-focused mobile development services for multiple
            platforms and use cases
          </p>
          <div className="mobile-app-services-grid">
            {whatWeOffer.map((item, idx) => (
              <div key={idx} className="mobile-app-service-card">
                <div className="mobile-app-service-icon">{item.icon}</div>
                <h3 className="mobile-app-service-title">{item.title}</h3>
                <p className="mobile-app-service-description">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Types */}
      <section className="mobile-app-section">
        <div className="mobile-app-container">
          <h2 className="mobile-app-section-title-center">
            Types of Apps We Build
          </h2>
          <p className="mobile-app-section-subtitle">
            From e-commerce to entertainment, we build apps for every industry
          </p>
          <div className="mobile-app-types-grid">
            {appTypes.map((app, idx) => (
              <div key={idx} className="mobile-app-type-card">
                <div className="mobile-app-type-icon">
                  <i className={`fas fa-${app.icon}`}></i>
                </div>
                <h3 className="mobile-app-type-title">{app.type}</h3>
                <p className="mobile-app-type-description">{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="mobile-app-section mobile-app-bg-light">
        <div className="mobile-app-container">
          <h2 className="mobile-app-section-title-center">
            Technologies We Use
          </h2>
          <p className="mobile-app-section-subtitle">
            We use proven frameworks and cloud services for secure, scalable
            mobile delivery
          </p>

          <div className="mobile-app-tech-tabs">
            {Object.keys(technologies).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`mobile-app-tech-tab ${
                  activeTab === tab ? "active" : ""
                }`}
              >
                {tab === "crossplatform"
                  ? "Cross-Platform"
                  : tab === "backend"
                  ? "Backend & Cloud"
                  : "Native Development"}
              </button>
            ))}
          </div>

          <div className="mobile-app-tech-grid">
            {technologies[activeTab].map((tech, idx) => (
              <div key={idx} className="mobile-app-tech-item">
                <div className="mobile-app-tech-logo-container">
                  <img
                    src={tech.logo}
                    alt={tech.name}
                    className="mobile-app-tech-logo"
                    onError={(e) => {
                      e.target.onerror = null;
                      const color =
                        tech.color ||
                        fallbackPalette[
                          Math.abs(tech.name.split("").reduce((acc, ch) => acc + ch.charCodeAt(0), 0)) %
                            fallbackPalette.length
                        ];
                      e.target.src = makeFallbackSvg(tech.name, color);
                    }}
                  />
                </div>
                <span className="mobile-app-tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="mobile-app-section">
        <div className="mobile-app-container">
          <h2 className="mobile-app-section-title-center">
            Our Development Process
          </h2>
          <p className="mobile-app-section-subtitle">
            From discovery to release, we manage delivery end-to-end
          </p>
          <div className="mobile-app-process-grid">
            {process.map((item, idx) => (
              <div key={idx} className="mobile-app-process-step">
                <div className="mobile-app-process-step-card">
                  <div className="mobile-app-process-step-number">
                    {item.step}
                  </div>
                  <h3 className="mobile-app-process-step-title">
                    {item.title}
                  </h3>
                  <p className="mobile-app-process-step-description">
                    {item.description}
                  </p>
                </div>
                {idx < process.length - 1 && (
                  <div className="mobile-app-process-arrow">
                    <ArrowRight className="mobile-app-arrow-icon" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section className="mobile-app-section mobile-app-bg-light">
        <div className="mobile-app-container">
          <h2 className="mobile-app-section-title-center">
            Choose Your Package
          </h2>
          <p className="mobile-app-section-subtitle">
            Engagement options aligned to app scope, complexity, and delivery
            timelines
          </p>
          <div className="mobile-app-packages-grid">
            {packages.map((pkg, idx) => (
              <div
                key={idx}
                className={`mobile-app-package-card ${
                  pkg.popular ? "popular" : ""
                }`}
              >
                {pkg.popular && (
                  <div className="mobile-app-popular-badge">MOST POPULAR</div>
                )}
                <h3 className="mobile-app-package-name">{pkg.name}</h3>
                <div className="mobile-app-package-price">{pkg.price}</div>
                <p className="mobile-app-package-ideal">{pkg.ideal}</p>

                <div className="mobile-app-package-features">
                  {pkg.features.map((feature, i) => (
                    <div key={i} className="mobile-app-package-feature">
                      {feature.included ? (
                        <Check className="mobile-app-feature-icon included" />
                      ) : (
                        <X className="mobile-app-feature-icon excluded" />
                      )}
                      <span
                        className={`mobile-app-feature-text ${
                          feature.included ? "" : "excluded"
                        }`}
                      >
                        {feature.text}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  className={`mobile-app-btn mobile-app-package-btn ${
                    pkg.popular
                      ? "mobile-app-btn-primary"
                      : "mobile-app-btn-secondary"
                  }`}
                  onClick={startProject}
                >
                  Discuss Requirements
                </button>
              </div>
            ))}
          </div>
          <p className="mobile-app-package-note">
            All packages include App Store optimization and 60 days of
            post-launch support
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="mobile-app-cta-section">
        <div className="mobile-app-container">
          <h2 className="mobile-app-cta-title">
            Plan Your Next Mobile Initiative
          </h2>
          <p className="mobile-app-cta-description">
            Discuss your roadmap with our team and get a clear delivery plan
            tailored to your business objectives.
          </p>
          <div className="mobile-app-cta-buttons">
            <button
              className="mobile-app-btn mobile-app-cta-btn-primary"
              onClick={startProject}
            >
              Request Consultation
            </button>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="footer-decoration"></div>
        <div className="footer-decoration"></div>

        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-brand">
                <img src="/TechXM-logo.png" alt="TECHXM logo" className="brand-logo-mark" width="30" height="30" />
              </div>
              <p className="footer-about">
                We design, build, and support secure digital systems for
                organizations that require reliability, scalability, and strong
                delivery governance.
              </p>
              <div className="footer-social">
                <a
                  href="https://www.linkedin.com/company/techxm/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a
                  href="https://www.instagram.com/techxm_za/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-instagram"></i>
                </a>
                <a
                  href="https://www.facebook.com/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-facebook"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li>
                  <a href="/web-development">
                    <i className="fas fa-chevron-right"></i>
                    Web Development
                  </a>
                </li>
                <li>
                  <a href="/mobile-app">
                    <i className="fas fa-chevron-right"></i>
                    Mobile Apps
                  </a>
                </li>
                <li>
                  <a href="/custom-software">
                    <i className="fas fa-chevron-right"></i>
                    Custom Software
                  </a>
                </li>
                <li>
                  <a href="/api-backend">
                    <i className="fas fa-chevron-right"></i>
                    API & Backend
                  </a>
                </li>
                <li>
                  <a href="/maintenance">
                    <i className="fas fa-chevron-right"></i>
                    Maintenance
                  </a>
                </li>
                <li>
                  <a href="/ai-services">
                    <i className="fas fa-chevron-right"></i>
                    AI & Machine Learning
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="/#about">
                    <i className="fas fa-chevron-right"></i>
                    About
                  </a>
                </li>
                <li>
                  <a href="/#faq">
                    <i className="fas fa-chevron-right"></i>
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="/track-project">
                    <i className="fas fa-chevron-right"></i>
                    Track Project
                  </a>
                </li>
                <li>
{/*
                  <a href="/#testimonials">
                    <i className="fas fa-chevron-right"></i>
                    Testimonials
                  </a>
*/}
                </li>
                <li>
                  <a href="/#contact">
                    <i className="fas fa-chevron-right"></i>
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Contact Info</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span> Ohenimuri, Walkerville</span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href="tel:0765536048">076 553 6048</a>
                  <span> / </span>
                  <a href="tel:0742686212">074 268 6212</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:info@techxm.co.za">info@techxm.co.za</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-clock"></i>
                  <span>Mon - Fri: 9:00 - 18:00</span>
                </div>
              </div>

              <div className="footer-newsletter">
                <p className="newsletter-text">Subscribe to our newsletter</p>
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <input
                    type="email"
                    name="newsletterEmail"
                    className="newsletter-input"
                    placeholder="Your email address"
                    required
                  />
                  <button type="submit" className="newsletter-btn">
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>
              &copy; 2025 TECHXM. All rights reserved. Enterprise Digital
              Solutions.
            </p>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
              <a href="/cookie-policy">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Remember to import your Navbar and Footer components */}
    </div>
  );
}








